import { type AppEnv, isHostedStaging, isLocalSupabaseUrl } from '@/platform/config/env'
import {
  createMaterialConsentApi,
  type MaterialConsentApi,
} from '@/platform/family/consent'
import {
  createDisabledExtractionService,
  type MaterialExtractionService,
} from '@/platform/materials/extraction'
import {
  createMaterialLearningApi,
  type MaterialLearningApi,
} from '@/platform/materials/learning'
import { createSupabaseExtractionService } from '@/platform/materials/supabase-extraction'
import { type AppSupabaseClient } from '@/platform/supabase/client'

import { isMockLearningEnabled } from './learning'

/**
 * Chooses whether school material may be sent for reading (Slice 3) and
 * learned from (D-17). Same boundary as the real learning service
 * (first-learning-journey §6.1), plus one hosted exception:
 *
 *   - pilot: disabled. Release mapping of school-material learning is open
 *     (D-14);
 *   - local / development: the Edge Functions, but only on a local Supabase
 *     stack. Any other URL (for example the hosted KT1 development project)
 *     is disabled, so no school document is ever sent to a hosted project
 *     from a development build;
 *   - staging: only the exact approved staging project
 *     (`src/platform/config/staging.ts`), for the invite-only family test.
 *     The server still decides everything: consent, entitlement, learner
 *     session, and English only for the D-20 pilot family. No fixture is
 *     honoured there (hosted URL), and no MOCK or DEMO content exists.
 *
 * There is no MOCK extraction: a reading is either real or not offered. The
 * DEMO samples (Slice 2) stay a separate, labelled path.
 */
export function materialBackendAllowed(env: AppEnv): boolean {
  if (isHostedStaging(env)) return true
  return (
    isMockLearningEnabled(env.VITE_APP_ENV) && isLocalSupabaseUrl(env.VITE_SUPABASE_URL)
  )
}

export function createMaterialExtractionService(
  env: AppEnv,
  client: AppSupabaseClient,
): MaterialExtractionService {
  return materialBackendAllowed(env)
    ? createSupabaseExtractionService(client)
    : createDisabledExtractionService()
}

/** Without a local stack (pilot, hosted URL): no lessons, no backend contact. */
export function createDisabledMaterialLearning(): MaterialLearningApi {
  const unavailable = () =>
    Promise.reject(new Error('material learning not available in this build'))
  return {
    forSession: () => ({
      start: unavailable,
      getPractice: unavailable,
      listMine: () => Promise.resolve([]),
      openHelp: unavailable,
      submit: unavailable,
      complete: unavailable,
    }),
    activity: () => Promise.resolve([]),
  }
}

/** Same boundary as `createMaterialExtractionService` (D-17). */
export function createMaterialLearning(
  env: AppEnv,
  client: AppSupabaseClient,
): MaterialLearningApi {
  return materialBackendAllowed(env)
    ? createMaterialLearningApi(client)
    : createDisabledMaterialLearning()
}

/** Parent consent (NFR-05): offered wherever material reading is; null otherwise. */
export function createMaterialConsent(
  env: AppEnv,
  client: AppSupabaseClient,
): MaterialConsentApi | null {
  return materialBackendAllowed(env) ? createMaterialConsentApi(client) : null
}
